import { useMemo, useRef } from "react";

import {
  DetailsRow,
  IColumn,
  ISelection,
  Link,
  ScrollablePane,
  Selection,
  SelectionMode,
  ShimmeredDetailsList,
  ShimmeredDetailsListProps,
  Stack,
  Text,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize
} from "@bps/fluent-ui";
import { HttpError } from "@bps/http-client";
import { IdentityIcon } from "@components/IdentityIcon";
import { ScrollListener } from "@components/tables/ScrollListener";
import { DeviceDetail } from "@libs/api/gateways/field/field-ops-gateway.dtos";

import { DeviceMultipleSiteIdComponent } from "./DeviceMultipleSiteIdComponent";

export interface DeviceSearchResultsTableProps
  extends ShimmeredDetailsListProps {
  isLoading?: boolean;
  error?: HttpError;
  hasNextPage: boolean | undefined;
  handleScrolledToBottom: () => Promise<void>;
  setSelectedDevices: (devices: DeviceDetail[]) => void;
  customColumns: string[];
}

export const DeviceSearchResultsTable = ({
  isLoading,
  error,
  hasNextPage,
  handleScrolledToBottom,
  setSelectedDevices,
  customColumns,
  ...props
}: DeviceSearchResultsTableProps) => {
  const selection = useRef(
    new Selection<DeviceDetail>({
      getKey: device => device.id,
      onSelectionChanged: () => {
        const rowSelections = selection.current.getSelection();
        setSelectedDevices(rowSelections);
      }
    })
  );

  const columns: IColumn[] = useMemo(() => {
    return [
      {
        key: "identIcon",
        name: "",
        minWidth: 50,
        maxWidth: 50,
        isResizable: false,
        onRender: (device: DeviceDetail) => (
          <IdentityIcon id={device.id} size={48} />
        )
      },
      {
        key: "name",
        name: "Identifiers",
        minWidth: 350,
        maxWidth: 350,
        isResizable: false,
        onRender: (device: DeviceDetail) => (
          <Stack>
            <Stack horizontal verticalAlign="center">
              <Text>{device.computerName}</Text>
              <TextBadge
                badgeSize={TextBadgeSize.small}
                badgeColor={
                  device.isActive ? TextBadgeColor.green : TextBadgeColor.red
                }
              >
                {device.isActive ? "Active" : "Inactive"}
              </TextBadge>
            </Stack>
            <Text variant="small">FieldDeviceId: {device.id}</Text>
            <Text variant="small">
              CloudConnectorId: {device.cloudConnectorId}
            </Text>
          </Stack>
        )
      },
      {
        key: "siteId",
        name: "Site ID",
        minWidth: 76,
        maxWidth: 150,
        isResizable: true,
        onRender: (device: DeviceDetail) => (
          <DeviceMultipleSiteIdComponent device={device} />
        )
      },
      {
        key: "siteName",
        name: "Site Name",
        minWidth: 80,
        maxWidth: 150,
        isResizable: true,
        onRender: (device: DeviceDetail) => <Text>{device.siteName}</Text>
      },
      {
        key: "link",
        name: "Device Page",
        minWidth: 90,
        maxWidth: 90,
        isResizable: false,
        onRender: (device: DeviceDetail) => (
          <Link href={`/field-mgmt/${device.id}/fd-overview`} target="_blank">
            Go to device
          </Link>
        )
      }
    ];
  }, []);

  const fullColumns = columns.concat(
    customColumns.map(customColumn => {
      return {
        key: customColumn,
        name: customColumn,
        minWidth: 100,
        maxWidth: 150,
        isResizable: true,
        onRender: (device: DeviceDetail) => {
          let text = "";
          const identifier = device.identifiers.find(
            x => x.identifierType === customColumn
          );
          if (identifier && !!identifier.identifierValue) {
            text = identifier.identifierValue;
          } else {
            const property = device.properties.find(
              x => x.propertyType === customColumn
            );
            if (property && !!property.propertyValue) {
              text = property.propertyValue ?? "";
            }
          }

          return <Text>{text}</Text>;
        }
      };
    })
  );

  return (
    <Stack
      verticalFill
      styles={{
        root: {
          position: "relative",
          height: "100%",
          flex: "1 1 0",
          overflowX: "auto"
        }
      }}
    >
      <ScrollablePane>
        <ShimmeredDetailsList
          enableShimmer={isLoading}
          errorMessage={error?.message}
          stickyHeader
          isSelectedOnFocus
          columns={fullColumns}
          onShouldVirtualize={() => true}
          shimmerLines={5}
          onRenderRow={row => (row ? <DetailsRow {...row} /> : null)}
          {...props}
          selectionMode={SelectionMode.multiple}
          selectionPreservedOnEmptyClick
          selection={selection.current as ISelection}
          detailsListStyles={{
            root: {
              overflow: "inherit"
            }
          }}
        />
        <ScrollListener
          hasNextPage={hasNextPage ?? false}
          onScrolledToBottom={handleScrolledToBottom}
        />
      </ScrollablePane>
    </Stack>
  );
};
